import React from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { CharCounter } from './CharCounter';

export interface InputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    // `children` / `dangerouslySetInnerHTML` come along with the DOM attribute
    // type but `<input>` is a void element — forwarding either throws at
    // runtime, so keep them a compile error the way the closed prop set did.
    'value' | 'onChange' | 'size' | 'type' | 'children' | 'dangerouslySetInnerHTML'
  > {
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  onClear?: () => void;
  showClearButton?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'date' | 'time' | 'datetime-local';
  leftIcon?: React.ComponentType<{ className?: string }>;
  rightIcon?: React.ComponentType<{ className?: string }>;
  id?: string;
  /**
   * Optional `name` attribute. When omitted, falls back to an auto-
   * derived value (`{id}-input` or `'input'`). Provide explicitly when
   * the form relies on the `name` (browser autofill, server-side form
   * serialization, accessibility tooling).
   */
  name?: string;
  autoComplete?: string;
  readOnly?: boolean;
  /**
   * Browser-enforced max input length. Pair with backend Zod `.max(N)`
   * matching the DB column width so the operator can't submit a value
   * the API will reject.
   */
  maxLength?: number;
  /**
   * When `maxLength` is set, a `{length}/{max}` counter renders below
   * the input by default. Set `false` to suppress (e.g., short fields
   * where the counter would clutter, or read-only displays).
   */
  showCounter?: boolean;
  /**
   * Description shown under the input. When a counter also renders, the
   * two share one row (helper left, counter right) instead of stacking —
   * so a field's footer stays a single line.
   */
  helperText?: React.ReactNode;
  /**
   * Invalid state. Paints the border, value text, placeholder and the
   * `helperText` in the error colour — so a field-level validation message
   * reads as one with the control, the way `TextArea`'s `error` prop already
   * does — and sets `aria-invalid`, so the state is not colour-only. Pass the
   * message as `helperText`; there is no separate error slot, because a
   * field never needs both a description and an error at once. Give the
   * input an `id` and the helper text is also linked as its description.
   */
  error?: boolean;
  /**
   * Listed for discoverability; every other standard `<input>` attribute
   * (`min`/`max`/`step`, `pattern`, `onPaste`, `aria-*`, `data-*`, ...) also
   * passes straight through to the element, as does `ref` via `forwardRef`.
   */
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  autoFocus?: boolean;
}

/**
 * The suite's text input. Use it instead of a raw `<input>` with ad-hoc Tailwind
 * classes — hand-rolled fields are what drove spacing and input-size apart
 * between form pages in the first place.
 *
 * `onChange` hands back the STRING VALUE, not the event. (`Checkbox` and
 * `Switch` hand back the event, so read `e.target.checked` there. The
 * difference trips people moving between the two.)
 *
 * Anything a plain `<input>` accepts passes through (`min`/`max`/`step`,
 * `pattern`, `onPaste`, `aria-*`, `data-*`), so a missing attribute is never a
 * reason to go raw. Four things still are, and the honest answer for them is
 * to keep that one control raw with an inline
 * `// eslint-disable-next-line no-restricted-syntax` and a reason:
 *   - a `type` this component doesn't model: `type` is a closed union, so
 *     `search`, `datetime-local`, `month` and `week` have no route through it
 *     and aren't in the lint rule's exempt list either. Widening the union is
 *     usually the better answer than a disable — it is one more entry here;
 *   - a field of a different height: `size` picks one of 32 / 38 / 44px and it
 *     is applied as an inline style, so no class can override it;
 *   - a borderless or transparent field: border, background, radius, padding
 *     and focus ring are classes, but `className` lands on the wrapper div as
 *     well as the input, so unpicking them means overriding each one (with its
 *     `dark:` / `focus:` variants) in both places — keep it raw;
 *   - an in-field overlay (a domain suffix sitting inside the field): the
 *     overlay is absolutely positioned against its wrapper, and this component
 *     inserts a `relative` wrapper of its own that the overlay then resolves
 *     against. `rightIcon` is not a way out — it takes a component, not text.
 * For a field with a fixed suffix (`subdomain` + `.help.airclou.com`), reach for
 * `InputWithSuffix` rather than composing it by hand.
 *
 * `date`, `time` and `datetime-local` DO have a route through this component
 * and should use it: seven hand-rolled ones had drifted into six different
 * styles across helpdesk, one of which named no background at all and rendered
 * as a white box in dark mode. They remain in the lint rule's exempt list so existing raw ones
 * don't error — that is migration debt, not permission to add more.
 *
 * Input types with no primitive — color, file, range, radio, hidden, image —
 * are exempt from the rule and stay raw.
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input({
  value,
  onChange,
  placeholder = "Enter text...",
  className,
  onClear,
  showClearButton = false,
  disabled = false,
  size = 'md',
  type = 'text',
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  id,
  name,
  autoComplete,
  readOnly = false,
  maxLength,
  showCounter = true,
  helperText,
  error = false,
  onKeyDown,
  onBlur,
  onFocus,
  autoFocus,
  ...rest
}, ref) {
  const sizeStyles = {
    sm: {
      height: '32px',
      padding: 'px-3 py-1.5',
      paddingLeft: 'pl-8',
      paddingRight: 'pr-8',
      iconSize: 'h-3 w-3',
      iconLeft: 'left-2.5',
      iconRight: 'right-2.5',
      clearRight: 'right-2'
    },
    md: {
      height: '38px',
      padding: 'px-4 py-2',
      paddingLeft: 'pl-10',
      paddingRight: 'pr-10',
      iconSize: 'h-4 w-4',
      iconLeft: 'left-3',
      iconRight: 'right-3',
      clearRight: 'right-3'
    },
    lg: {
      height: '44px',
      padding: 'px-5 py-3',
      paddingLeft: 'pl-12',
      paddingRight: 'pr-12',
      iconSize: 'h-5 w-5',
      iconLeft: 'left-3.5',
      iconRight: 'right-3.5',
      clearRight: 'right-3.5'
    }
  };

  const styles = sizeStyles[size];
  // Narrowed once so the counter branches get `number`, not `number | undefined`
  // behind a non-null assertion that a later edit could quietly invalidate.
  const counterMax = typeof maxLength === 'number' && showCounter ? maxLength : null;
  const hasClearButton = showClearButton && value && onClear;
  const hasLeftIcon = LeftIcon;
  const hasRightIcon = RightIcon || hasClearButton;

  // Determine padding based on icons
  let paddingClass = styles.padding;
  if (hasLeftIcon && hasRightIcon) {
    paddingClass = `${styles.paddingLeft} ${styles.paddingRight}`;
  } else if (hasLeftIcon) {
    paddingClass = `${styles.paddingLeft} pr-4`;
  } else if (hasRightIcon) {
    paddingClass = `pl-4 ${styles.paddingRight}`;
  }

  // Lets assistive tech read the helper text (and so the error message) as
  // the field's description. Needs an id to point at, so it is only wired
  // when the call site gave the input one.
  const helperId = helperText && id ? `${id}-helper` : undefined;

  return (
    <div className={cn('relative', className)}>
      {/* Left Icon */}
      {LeftIcon && (
        <LeftIcon className={cn(
          'absolute top-1/2 transform -translate-y-1/2 text-gray-400',
          styles.iconSize,
          styles.iconLeft
        )} />
      )}

      {/* Input */}
      <input
        ref={ref}
        id={id}
        name={name ?? (id ? `${id}-input` : 'input')}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        onFocus={onFocus}
        autoFocus={autoFocus}
        disabled={disabled}
        readOnly={readOnly}
        autoComplete={autoComplete}
        maxLength={maxLength}
        className={cn(
          // `color-scheme` is what makes the browser draw its OWN chrome dark:
          // the date/time picker glyph, the spinner on number fields. Without it
          // that glyph is a dark SVG on a dark field — invisible, and unreachable
          // from any ordinary colour class because we never paint it.
          // field-text (styles/fields.css): 14px, and 16px on phones and other
          // touch devices so iOS Safari doesn't zoom the page on focus.
          'w-full border rounded-md bg-background dark:bg-white/5 dark:[color-scheme:dark] focus:outline-none focus:ring-2 focus:border-transparent dark:focus:border-transparent field-text transition-colors',
          // Its opacity IS reachable, though, and dimming it lands on ~#9CA3AF
          // in both themes — `text-gray-400`, the colour Select paints its
          // chevron, so the two icons carry the same weight in a row of fields.
          // WebKit/Blink only; Firefox exposes no equivalent pseudo-element, so
          // there the glyph stays as the browser draws it.
          '[&::-webkit-calendar-picker-indicator]:opacity-40 dark:[&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:cursor-pointer',
          // The same error palette TextArea applies — border, value text,
          // placeholder and focus ring — so the two primitives agree inside
          // one form.
          error
            ? 'border-red-300 dark:border-red-600 text-red-900 dark:text-red-100 placeholder:text-red-400 dark:placeholder:text-red-500 focus:ring-red-500'
            : 'border-field text-gray-900 dark:text-foreground placeholder:text-gray-400 dark:placeholder:text-muted-foreground focus:ring-primary',
          // A date/time control has no ::placeholder. The browser draws its own
          // `dd/mm/yyyy` hint using the element's `color`, so an empty one sits
          // at full value brightness beside a text field whose placeholder is
          // muted — they read as different kinds of content. Mute the text
          // while there is no value so the hint looks like the placeholder it
          // is, and snaps to normal weight once a date is picked. Not applied
          // under `error`, where the red text is the signal.
          !error && !value && (type === 'date' || type === 'time' || type === 'datetime-local')
            && 'text-gray-400 dark:text-muted-foreground',
          paddingClass,
          disabled && !readOnly && 'opacity-50 cursor-not-allowed',
          readOnly && 'cursor-default bg-gray-50 dark:bg-transparent',
          className
        )}
        {...rest}
        // These three come last because the component and the caller can each
        // have a legitimate value, so they are merged rather than won. Spread
        // ahead of `rest` they were silently overwritten by the component's
        // own `undefined` — a caller's `aria-describedby` simply vanished.
        aria-invalid={error || rest['aria-invalid']}
        aria-describedby={[rest['aria-describedby'], helperId].filter(Boolean).join(' ') || undefined}
        // `size`'s height stays authoritative — a field of a different height is
        // one of the documented reasons to keep a control raw.
        style={{ ...rest.style, height: styles.height }}
      />

      {/* Right Icon */}
      {RightIcon && !hasClearButton && (
        <RightIcon className={cn(
          'absolute top-1/2 transform -translate-y-1/2 text-gray-400',
          styles.iconSize,
          styles.iconRight
        )} />
      )}

      {/* Clear Button */}
      {hasClearButton && (
        <button
          onClick={onClear}
          className={cn(
            'absolute top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors',
            styles.clearRight
          )}
          type="button"
          id={id ? `${id}-clear` : 'input-clear'}
          name={id ? `${id}-clear` : 'input-clear'}
        >
          <X className="h-4 w-4" />
        </button>
      )}

      {/* Footer — helper text and/or the character counter, below the
          input. When both are present they share one row (helper left,
          counter right); either alone renders as a block.

          Caveat: the wrapper is the icons' positioning context, so a
          footer makes it taller and the `top-1/2` icons re-center against
          the taller box rather than the input. No call site pairs an icon
          with a counter or helper text today; pairing them needs the
          input + icons moved into their own relative wrapper first. */}
      {helperText && counterMax !== null ? (
        <div className="mt-1 flex items-start justify-between gap-3">
          <p id={helperId} className={cn('text-xs', error ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground')}>
            {helperText}
          </p>
          <CharCounter length={(value ?? '').length} maxLength={counterMax} inline />
        </div>
      ) : (
        <>
          {helperText && (
            <p id={helperId} className={cn('mt-1 text-xs', error ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground')}>
              {helperText}
            </p>
          )}
          {counterMax !== null && (
            <CharCounter length={(value ?? '').length} maxLength={counterMax} />
          )}
        </>
      )}
    </div>
  );
});

Input.displayName = 'Input';